// Phase 0.5 service layer — typed API access for the platform engines.
import api from "@/lib/api";

export const navigationService = {
  resolve: () => api.get("/v1/navigation").then((r) => r.data.data),
};

export const dashboardService = {
  me: () => api.get("/v1/dashboards/me").then((r) => r.data.data),
  kpi: (key) => api.get(`/v1/kpi/${key}`).then((r) => r.data.data),
};

export const notificationService = {
  list: (params) => api.get("/v1/notifications", { params }).then((r) => r.data.data),
  markRead: (id) => api.patch(`/v1/notifications/${id}/read`),
  markAll: () => api.post("/v1/notifications/read-all"),
  send: (body) => api.post("/v1/notifications/send", body).then((r) => r.data.data),
  templates: () => api.get("/v1/notification-templates").then((r) => r.data.data),
};

export const fileService = {
  list: (params) => api.get("/v1/files", { params }).then((r) => r.data.data),
  upload: (formData, onProgress) =>
    api.post("/v1/files/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
    }).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/files/${id}`),
  download: (id) => api.get(`/v1/files/${id}/download`, { responseType: "blob" }),
};

export const aiService = {
  providers: () => api.get("/v1/ai/providers").then((r) => r.data.data),
  updateProvider: (id, body) => api.put(`/v1/ai/providers/${id}`, body),
  testProvider: (id) => api.post(`/v1/ai/providers/${id}/test`).then((r) => r.data.data),
  models: () => api.get("/v1/ai/models").then((r) => r.data.data),
  updateModel: (id, body) => api.put(`/v1/ai/models/${id}`, body),
  usage: () => api.get("/v1/ai/usage").then((r) => r.data.data),
  audit: () => api.get("/v1/ai/audit").then((r) => r.data.data),
};

export const sessionService = {
  list: () => api.get("/auth/sessions").then((r) => r.data.data),
  revoke: (id) => api.delete(`/auth/sessions/${id}`),
};

// ---- Phase 1: Customer Data Platform ----
export const customerService = {
  list: (params) => api.get("/v1/customers", { params }).then((r) => r.data.data),
  stats: (params) => api.get("/v1/customers/stats", { params }).then((r) => r.data.data),
  insights: (params) => api.get("/v1/customers/insights", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/customers/${id}`).then((r) => r.data.data),
  contacts: (id) => api.get(`/v1/customers/${id}/contacts`).then((r) => r.data.data),
  create: (body) => api.post("/v1/customers", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/customers/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/customers/${id}`),
  // Phase 2
  notes: (id) => api.get(`/v1/customers/${id}/notes`).then((r) => r.data.data),
  addNote: (id, body) => api.post(`/v1/customers/${id}/notes`, body).then((r) => r.data.data),
  deleteNote: (id, noteId) => api.delete(`/v1/customers/${id}/notes/${noteId}`),
  setTags: (id, tags) => api.put(`/v1/customers/${id}/tags`, { tags }).then((r) => r.data.data),
  assignSegment: (id, segmentId) => api.post(`/v1/customers/${id}/segments`, { segmentId }),
  removeSegment: (id, segmentId) => api.delete(`/v1/customers/${id}/segments/${segmentId}`),
  getPreferences: (id) => api.get(`/v1/customers/${id}/preferences`).then((r) => r.data.data),
  savePreferences: (id, body) => api.put(`/v1/customers/${id}/preferences`, body).then((r) => r.data.data),
};

export const addressService = {
  list: (customerId) => api.get("/v1/addresses", { params: { customerId } }).then((r) => r.data.data),
  create: (body) => api.post("/v1/addresses", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/addresses/${id}`, body).then((r) => r.data.data),
  setPrimary: (id) => api.patch(`/v1/addresses/${id}/primary`),
  remove: (id) => api.delete(`/v1/addresses/${id}`),
};

export const segmentService = {
  list: (params) => api.get("/v1/segments", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/segments/${id}`).then((r) => r.data.data),
  members: (id) => api.get(`/v1/segments/${id}/members`).then((r) => r.data.data),
  create: (body) => api.post("/v1/segments", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/segments/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/segments/${id}`),
};

export const contactService = {
  list: (params) => api.get("/v1/contacts", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/contacts/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/contacts", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/contacts/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/contacts/${id}`),
  setPrimary: (id) => api.patch(`/v1/contacts/${id}/primary`),
};

export const tenantService = {
  list: () => api.get("/v1/tenants").then((r) => r.data.data),
};

export const userService = {
  list: (params) => api.get("/v1/users", { params }).then((r) => r.data.data),
};

export const auditService = {
  list: (params) => api.get("/v1/audit", { params }).then((r) => r.data.data),
};

// ---- Phase 3: CRM Module ----
export const crmService = {
  dashboard: (params) => api.get("/v1/crm/dashboard", { params }).then((r) => r.data.data),
};

export const leadService = {
  list: (params) => api.get("/v1/leads", { params }).then((r) => r.data.data),
  stats: (params) => api.get("/v1/leads/stats", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/leads/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/leads", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/leads/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/leads/${id}`),
  assign: (id, assignedTo) => api.patch(`/v1/leads/${id}/assign`, { assignedTo }).then((r) => r.data.data),
  qualify: (id, body) => api.patch(`/v1/leads/${id}/qualify`, body).then((r) => r.data.data),
  convert: (id, body) => api.post(`/v1/leads/${id}/convert`, body).then((r) => r.data.data),
  activities: (id) => api.get(`/v1/leads/${id}/activities`).then((r) => r.data.data),
  addActivity: (id, body) => api.post(`/v1/leads/${id}/activities`, body).then((r) => r.data.data),
  getScore: (id) => api.get(`/v1/leads/${id}/score`).then((r) => r.data.data),
  setScore: (id, body) => api.post(`/v1/leads/${id}/score`, body).then((r) => r.data.data),
};

export const leadSourceService = {
  list: (params) => api.get("/v1/lead-sources", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/lead-sources", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/lead-sources/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/lead-sources/${id}`),
};

export const opportunityService = {
  list: (params) => api.get("/v1/opportunities", { params }).then((r) => r.data.data),
  pipeline: (params) => api.get("/v1/opportunities/pipeline", { params }).then((r) => r.data.data),
  forecast: (params) => api.get("/v1/opportunities/forecast", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/opportunities/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/opportunities", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/opportunities/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/opportunities/${id}`),
  moveStage: (id, stage) => api.patch(`/v1/opportunities/${id}/stage`, { stage }).then((r) => r.data.data),
};

export const salesActivityService = {
  list: (params) => api.get("/v1/sales-activities", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/sales-activities", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/sales-activities/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/sales-activities/${id}`),
};

export const taskService = {
  list: (params) => api.get("/v1/tasks", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/tasks", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/tasks/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/tasks/${id}`),
  complete: (id) => api.patch(`/v1/tasks/${id}/complete`).then((r) => r.data.data),
};

// ---- Phase 4: Omnichannel Inbox ----
export const channelService = {
  list: (params) => api.get("/v1/channels", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/channels", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/channels/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/channels/${id}`),
};

export const conversationService = {
  list: (params) => api.get("/v1/conversations", { params }).then((r) => r.data.data),
  stats: (params) => api.get("/v1/conversations/stats", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/conversations/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/conversations", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/conversations/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/conversations/${id}`),
  assign: (id, agentId) => api.patch(`/v1/conversations/${id}/assign`, { agentId }).then((r) => r.data.data),
  linkCustomer: (id, customerId) => api.patch(`/v1/conversations/${id}/link-customer`, { customerId }).then((r) => r.data.data),
  setTags: (id, tags) => api.put(`/v1/conversations/${id}/tags`, { tags }).then((r) => r.data.data),
  notes: (id) => api.get(`/v1/conversations/${id}/notes`).then((r) => r.data.data),
  addNote: (id, body) => api.post(`/v1/conversations/${id}/notes`, { body }).then((r) => r.data.data),
  deleteNote: (id, noteId) => api.delete(`/v1/conversations/${id}/notes/${noteId}`),
  messages: (id) => api.get(`/v1/conversations/${id}/messages`).then((r) => r.data.data),
  sendMessage: (id, body) => api.post(`/v1/conversations/${id}/messages`, body).then((r) => r.data.data),
  sendWhatsapp: (id, body) => api.post(`/v1/conversations/${id}/whatsapp/send`, body).then((r) => r.data.data),
  markRead: (id) => api.patch(`/v1/conversations/${id}/read`),
};

// ---- Phase 5: WhatsApp Business Integration ----
export const whatsappService = {
  getConfig: (cid) => api.get(`/v1/channels/${cid}/whatsapp/config`).then((r) => r.data.data),
  connect: (cid, body) => api.post(`/v1/channels/${cid}/whatsapp/connect`, body).then((r) => r.data.data),
  test: (cid, body) => api.post(`/v1/channels/${cid}/whatsapp/test`, body).then((r) => r.data.data),
  simulateInbound: (cid, body) => api.post(`/v1/channels/${cid}/whatsapp/simulate-inbound`, body).then((r) => r.data.data),
};

// ---- Phase 6: Customer Service & Ticketing ----
export const ticketService = {
  list: (params) => api.get("/v1/tickets", { params }).then((r) => r.data.data),
  stats: (params) => api.get("/v1/tickets/stats", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/tickets/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/tickets", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/tickets/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/tickets/${id}`),
  assign: (id, body) => api.patch(`/v1/tickets/${id}/assign`, body).then((r) => r.data.data),
  setStatus: (id, body) => api.patch(`/v1/tickets/${id}/status`, body).then((r) => r.data.data),
  setPriority: (id, body) => api.patch(`/v1/tickets/${id}/priority`, body).then((r) => r.data.data),
  escalate: (id, body) => api.patch(`/v1/tickets/${id}/escalate`, body).then((r) => r.data.data),
  link: (id, body) => api.patch(`/v1/tickets/${id}/link`, body).then((r) => r.data.data),
  comments: (id) => api.get(`/v1/tickets/${id}/comments`).then((r) => r.data.data),
  addComment: (id, body) => api.post(`/v1/tickets/${id}/comments`, body).then((r) => r.data.data),
  deleteComment: (id, cid) => api.delete(`/v1/tickets/${id}/comments/${cid}`),
  attachments: (id) => api.get(`/v1/tickets/${id}/attachments`).then((r) => r.data.data),
  addAttachment: (id, body) => api.post(`/v1/tickets/${id}/attachments`, body).then((r) => r.data.data),
  deleteAttachment: (id, aid) => api.delete(`/v1/tickets/${id}/attachments/${aid}`),
  timeline: (id) => api.get(`/v1/tickets/${id}/timeline`).then((r) => r.data.data),
  feedback: (id, body) => api.post(`/v1/tickets/${id}/feedback`, body).then((r) => r.data.data),
};

export const caseService = {
  list: (params) => api.get("/v1/cases", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/cases/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/cases", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/cases/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/cases/${id}`),
  manageTickets: (id, body) => api.patch(`/v1/cases/${id}/tickets`, body).then((r) => r.data.data),
};

export const slaService = {
  list: (params) => api.get("/v1/sla-policies", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/sla-policies", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/sla-policies/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/sla-policies/${id}`),
};

export const escalationService = {
  list: (params) => api.get("/v1/escalation-rules", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/escalation-rules", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/escalation-rules/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/escalation-rules/${id}`),
};

export const serviceService = {
  dashboard: (params) => api.get("/v1/service/dashboard", { params }).then((r) => r.data.data),
  feedback: (params) => api.get("/v1/feedback", { params }).then((r) => r.data.data),
  publicInfo: (token) => api.get(`/v1/feedback/public/${token}`).then((r) => r.data.data),
  publicSubmit: (token, body) => api.post(`/v1/feedback/public/${token}`, body).then((r) => r.data.data),
};

// ---- Phase 7: Campaign Management ----
export const campaignService = {
  list: (params) => api.get("/v1/campaigns", { params }).then((r) => r.data.data),
  stats: (params) => api.get("/v1/campaigns/stats", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/campaigns/${id}`).then((r) => r.data.data),
  create: (body) => api.post("/v1/campaigns", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/campaigns/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/campaigns/${id}`),
  setSegments: (id, body) => api.patch(`/v1/campaigns/${id}/segments`, body).then((r) => r.data.data),
  audience: (id) => api.get(`/v1/campaigns/${id}/audience`).then((r) => r.data.data),
  submit: (id) => api.patch(`/v1/campaigns/${id}/submit`).then((r) => r.data.data),
  approve: (id) => api.patch(`/v1/campaigns/${id}/approve`).then((r) => r.data.data),
  reject: (id, body) => api.patch(`/v1/campaigns/${id}/reject`, body).then((r) => r.data.data),
  schedule: (id, body) => api.patch(`/v1/campaigns/${id}/schedule`, body).then((r) => r.data.data),
  pause: (id) => api.patch(`/v1/campaigns/${id}/pause`).then((r) => r.data.data),
  cancel: (id) => api.patch(`/v1/campaigns/${id}/cancel`).then((r) => r.data.data),
  execute: (id) => api.post(`/v1/campaigns/${id}/execute`).then((r) => r.data.data),
  responses: (id, params) => api.get(`/v1/campaigns/${id}/responses`, { params }).then((r) => r.data.data),
  analytics: (id) => api.get(`/v1/campaigns/${id}/analytics`).then((r) => r.data.data),
  optoutInfo: (token) => api.get(`/v1/campaigns/optout/public/${token}`).then((r) => r.data.data),
  optoutSubmit: (token) => api.post(`/v1/campaigns/optout/public/${token}`).then((r) => r.data.data),
};

export const commTemplateService = {
  list: (params) => api.get("/v1/comm-templates", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/comm-templates", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/comm-templates/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/comm-templates/${id}`),
  submitWhatsapp: (id, body) => api.post(`/v1/comm-templates/${id}/whatsapp/submit`, body).then((r) => r.data.data),
  whatsappStatus: (id, channelId) => api.get(`/v1/comm-templates/${id}/whatsapp/status`, { params: { channelId } }).then((r) => r.data.data),
};

export const routingRuleService = {
  list: (params) => api.get("/v1/routing-rules", { params }).then((r) => r.data.data),
  create: (body) => api.post("/v1/routing-rules", body).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/routing-rules/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/routing-rules/${id}`),
};

export const inboxService = {
  stats: (params) => api.get("/v1/inbox/stats", { params }).then((r) => r.data.data),
};


// ===== Phase 8 — Knowledge Management =====
export const knowledgeService = {
  dashboard: (params) => api.get("/v1/knowledge/dashboard", { params }).then((r) => r.data.data),
  search: (params) => api.get("/v1/knowledge/search", { params }).then((r) => r.data.data),
  // Articles
  listArticles: (params) => api.get("/v1/knowledge/articles", { params }).then((r) => r.data.data),
  getArticle: (id) => api.get(`/v1/knowledge/articles/${id}`).then((r) => r.data.data),
  createArticle: (body) => api.post("/v1/knowledge/articles", body).then((r) => r.data.data),
  updateArticle: (id, body) => api.put(`/v1/knowledge/articles/${id}`, body).then((r) => r.data.data),
  removeArticle: (id) => api.delete(`/v1/knowledge/articles/${id}`),
  versions: (id) => api.get(`/v1/knowledge/articles/${id}/versions`).then((r) => r.data.data),
  restoreVersion: (id, v) => api.post(`/v1/knowledge/articles/${id}/restore/${v}`).then((r) => r.data.data),
  chunks: (id) => api.get(`/v1/knowledge/articles/${id}/chunks`).then((r) => r.data.data),
  usage: (id) => api.get(`/v1/knowledge/articles/${id}/usage`).then((r) => r.data.data),
  articleAction: (id, action, body) => api.patch(`/v1/knowledge/articles/${id}/${action}`, body).then((r) => r.data.data),
  // FAQs
  listFaqs: (params) => api.get("/v1/knowledge/faqs", { params }).then((r) => r.data.data),
  createFaq: (body) => api.post("/v1/knowledge/faqs", body).then((r) => r.data.data),
  updateFaq: (id, body) => api.put(`/v1/knowledge/faqs/${id}`, body).then((r) => r.data.data),
  removeFaq: (id) => api.delete(`/v1/knowledge/faqs/${id}`),
  faqAction: (id, action, body) => api.patch(`/v1/knowledge/faqs/${id}/${action}`, body).then((r) => r.data.data),
  // Categories
  listCategories: (params) => api.get("/v1/knowledge/categories", { params }).then((r) => r.data.data),
  createCategory: (body) => api.post("/v1/knowledge/categories", body).then((r) => r.data.data),
  updateCategory: (id, body) => api.put(`/v1/knowledge/categories/${id}`, body).then((r) => r.data.data),
  removeCategory: (id) => api.delete(`/v1/knowledge/categories/${id}`),
  // Tags
  listTags: (params) => api.get("/v1/knowledge/tags", { params }).then((r) => r.data.data),
  createTag: (body) => api.post("/v1/knowledge/tags", body).then((r) => r.data.data),
  updateTag: (id, body) => api.put(`/v1/knowledge/tags/${id}`, body).then((r) => r.data.data),
  removeTag: (id) => api.delete(`/v1/knowledge/tags/${id}`),
  // Usage
  recordUsage: (body) => api.post("/v1/knowledge/usage", body).then((r) => r.data.data),
};

export const knowledgeDocService = {
  list: (params) => api.get("/v1/knowledge/documents", { params }).then((r) => r.data.data),
  get: (id) => api.get(`/v1/knowledge/documents/${id}`).then((r) => r.data.data),
  upload: (formData, onProgress) => api.post("/v1/knowledge/documents/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: onProgress,
  }).then((r) => r.data.data),
  uploadVersion: (id, formData) => api.post(`/v1/knowledge/documents/${id}/version`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data.data),
  update: (id, body) => api.put(`/v1/knowledge/documents/${id}`, body).then((r) => r.data.data),
  remove: (id) => api.delete(`/v1/knowledge/documents/${id}`),
  download: (id) => api.get(`/v1/knowledge/documents/${id}/download`, { responseType: "blob" }),
  action: (id, action, body) => api.patch(`/v1/knowledge/documents/${id}/${action}`, body).then((r) => r.data.data),
};

// ===== Phase 9 — AI Copilot Foundation =====
export const aiCopilotService = {
  query: (body) => api.post("/v1/ai/copilot/query", body).then((r) => r.data.data),
  listConversations: () => api.get("/v1/ai/copilot/conversations").then((r) => r.data.data),
  getConversation: (id) => api.get(`/v1/ai/copilot/conversations/${id}`).then((r) => r.data.data),
  deleteConversation: (id) => api.delete(`/v1/ai/copilot/conversations/${id}`),
  feedback: (messageId, body) => api.post(`/v1/ai/copilot/messages/${messageId}/feedback`, body).then((r) => r.data.data),
  summarize: (body) => api.post("/v1/ai/copilot/summarize", body).then((r) => r.data.data),
  recommendations: (params) => api.get("/v1/ai/copilot/recommendations", { params }).then((r) => r.data.data),
  audit: (params) => api.get("/v1/ai/copilot/audit", { params }).then((r) => r.data.data),
};

// Phase 10 — Agentic AI Agents
const A = "/v1/ai/agents";
export const aiAgentService = {
  center: (params) => api.get(A, { params }).then((r) => r.data.data),
  dashboard: (params) => api.get(`${A}/dashboard`, { params }).then((r) => r.data.data),
  get: (code, params) => api.get(`${A}/${code}`, { params }).then((r) => r.data.data),
  configure: (code, body, params) => api.put(`${A}/${code}/config`, body, { params }).then((r) => r.data.data),
  run: (code, body) => api.post(`${A}/${code}/run`, body).then((r) => r.data.data),
  memory: (code, params) => api.get(`${A}/${code}/memory`, { params }).then((r) => r.data.data),
  recommendations: (params) => api.get(`${A}/recommendations`, { params }).then((r) => r.data.data),
  acceptRec: (id) => api.patch(`${A}/recommendations/${id}/accept`).then((r) => r.data.data),
  dismissRec: (id) => api.patch(`${A}/recommendations/${id}/dismiss`).then((r) => r.data.data),
  actions: (params) => api.get(`${A}/actions`, { params }).then((r) => r.data.data),
  executeAction: (id) => api.patch(`${A}/actions/${id}/execute`).then((r) => r.data.data),
  approvals: (params) => api.get(`${A}/approvals`, { params }).then((r) => r.data.data),
  approve: (id, body) => api.patch(`${A}/approvals/${id}/approve`, body || {}).then((r) => r.data.data),
  reject: (id, body) => api.patch(`${A}/approvals/${id}/reject`, body || {}).then((r) => r.data.data),
  runs: (params) => api.get(`${A}/runs`, { params }).then((r) => r.data.data),
};

// Phase 11 — Enterprise Integration Fabric
const I = "/v1/integrations";
export const integrationService = {
  dashboard: (params) => api.get(`${I}/dashboard`, { params }).then((r) => r.data.data),
  monitoring: (params) => api.get(`${I}/monitoring`, { params }).then((r) => r.data.data),
  connectors: () => api.get(`${I}/connectors`).then((r) => r.data.data),
  list: (params) => api.get(I, { params }).then((r) => r.data.data),
  create: (body) => api.post(I, body).then((r) => r.data.data),
  get: (id, params) => api.get(`${I}/${id}`, { params }).then((r) => r.data.data),
  update: (id, body, params) => api.put(`${I}/${id}`, body, { params }).then((r) => r.data.data),
  remove: (id, params) => api.delete(`${I}/${id}`, { params }).then((r) => r.data.data),
  connect: (id, body, params) => api.post(`${I}/${id}/connect`, body, { params }).then((r) => r.data.data),
  credentials: (id, params) => api.get(`${I}/${id}/credentials`, { params }).then((r) => r.data.data),
  test: (id, params) => api.post(`${I}/${id}/test`, {}, { params }).then((r) => r.data.data),
  upload: (id, formData, params) => api.post(`${I}/${id}/upload`, formData, {
    params, headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data.data),
  sample: (id, params) => api.get(`${I}/${id}/sample`, { params }).then((r) => r.data.data),
  mappings: (id, params) => api.get(`${I}/${id}/mappings`, { params }).then((r) => r.data.data),
  saveMapping: (id, target, body, params) => api.put(`${I}/${id}/mappings/${target}`, body, { params }).then((r) => r.data.data),
  previewMapping: (id, body, params) => api.post(`${I}/${id}/mappings/preview`, body, { params }).then((r) => r.data.data),
  syncJobs: (params) => api.get(`${I}/sync-jobs`, { params }).then((r) => r.data.data),
  createJob: (body) => api.post(`${I}/sync-jobs`, body).then((r) => r.data.data),
  getJob: (id, params) => api.get(`${I}/sync-jobs/${id}`, { params }).then((r) => r.data.data),
  runJob: (id, params) => api.post(`${I}/sync-jobs/${id}/run`, {}, { params }).then((r) => r.data.data),
  enableJob: (id, params) => api.patch(`${I}/sync-jobs/${id}/enable`, {}, { params }).then((r) => r.data.data),
  disableJob: (id, params) => api.patch(`${I}/sync-jobs/${id}/disable`, {}, { params }).then((r) => r.data.data),
  deleteJob: (id, params) => api.delete(`${I}/sync-jobs/${id}`, { params }).then((r) => r.data.data),
  retryFailed: (id, params) => api.post(`${I}/sync-jobs/${id}/retry-failed`, {}, { params }).then((r) => r.data.data),
  syncLogs: (params) => api.get(`${I}/sync-logs`, { params }).then((r) => r.data.data),
  errorLogs: (params) => api.get(`${I}/error-logs`, { params }).then((r) => r.data.data),
  retryError: (id, params) => api.patch(`${I}/error-logs/${id}/retry`, {}, { params }).then((r) => r.data.data),
  apiTransactions: (params) => api.get(`${I}/api-transactions`, { params }).then((r) => r.data.data),
};

// Phase 12 — Executive Command Center
const X = "/v1/exec";
export const execService = {
  ceo: (params) => api.get(`${X}/ceo`, { params }).then((r) => r.data.data),
  sales: (params) => api.get(`${X}/sales`, { params }).then((r) => r.data.data),
  marketing: (params) => api.get(`${X}/marketing`, { params }).then((r) => r.data.data),
  service: (params) => api.get(`${X}/service`, { params }).then((r) => r.data.data),
  collections: (params) => api.get(`${X}/collections`, { params }).then((r) => r.data.data),
  riskRadar: (params) => api.get(`${X}/risk-radar`, { params }).then((r) => r.data.data),
  opportunityRadar: (params) => api.get(`${X}/opportunity-radar`, { params }).then((r) => r.data.data),
  campaignSummary: (params) => api.get(`${X}/campaign-summary`, { params }).then((r) => r.data.data),
  customerHealth: (params) => api.get(`${X}/customer-health`, { params }).then((r) => r.data.data),
  alerts: (params) => api.get(`${X}/alerts`, { params }).then((r) => r.data.data),
  actionPlan: (params) => api.get(`${X}/action-plan`, { params }).then((r) => r.data.data),
  drilldown: (metric, params) => api.get(`${X}/drilldown/${metric}`, { params }).then((r) => r.data.data),
  aiSummary: (body) => api.post(`${X}/ai-summary`, body).then((r) => r.data.data),
  digest: (params) => api.get(`${X}/digest`, { params }).then((r) => r.data.data),
  runDigest: (body) => api.post(`${X}/digest/run`, body).then((r) => r.data.data),
};

// ---- Organization (self-service tenant settings: Regional → Currency) ----
export const organizationService = {
  regionalSettings: () => api.get("/v1/organization/regional-settings").then((r) => r.data.data),
  setCurrency: (currency) =>
    api.put("/v1/organization/regional-settings/currency", { currency }).then((r) => r.data),
};

// ---- ReasonLoyaltyIQ — Loyalty & Rewards Intelligence (pack-gated add-on) ----
export const loyaltyService = {
  dashboard: (params) => api.get("/v1/loyalty/dashboard", { params }).then((r) => r.data.data),
  programs: (params) => api.get("/v1/loyalty/programs", { params }).then((r) => r.data.data),
  createProgram: (b) => api.post("/v1/loyalty/programs", b).then((r) => r.data.data),
  updateProgram: (id, b) => api.put(`/v1/loyalty/programs/${id}`, b).then((r) => r.data.data),
  deleteProgram: (id) => api.delete(`/v1/loyalty/programs/${id}`),
  rules: (params) => api.get("/v1/loyalty/rules", { params }).then((r) => r.data.data),
  ruleCatalog: () => api.get("/v1/loyalty/rules/catalog").then((r) => r.data.data),
  createRule: (b) => api.post("/v1/loyalty/rules", b).then((r) => r.data.data),
  updateRule: (id, b) => api.put(`/v1/loyalty/rules/${id}`, b).then((r) => r.data.data),
  deleteRule: (id) => api.delete(`/v1/loyalty/rules/${id}`),
  wallets: (params) => api.get("/v1/loyalty/wallets", { params }).then((r) => r.data.data),
  transactions: (params) => api.get("/v1/loyalty/transactions", { params }).then((r) => r.data.data),
  customerSummary: (cid) => api.get(`/v1/loyalty/customers/${cid}/summary`).then((r) => r.data.data),
  createTransaction: (cid, b) => api.post(`/v1/loyalty/customers/${cid}/transactions`, b).then((r) => r.data.data),
  packConfig: () => api.get("/v1/loyalty/pack").then((r) => r.data.data),
  togglePack: (tid, enabled) => api.post(`/v1/loyalty/pack/tenants/${tid}`, { enabled }).then((r) => r.data),
  setIndustryMapping: (mapping) => api.put("/v1/loyalty/pack/industry-mapping", { mapping }).then((r) => r.data.data),
  // Phase 2 — Membership/Tiers
  tiers: () => api.get("/v1/loyalty/tiers").then((r) => r.data.data),
  seedTiers: () => api.post("/v1/loyalty/tiers/seed-defaults").then((r) => r.data.data),
  createTier: (b) => api.post("/v1/loyalty/tiers", b).then((r) => r.data.data),
  updateTier: (id, b) => api.put(`/v1/loyalty/tiers/${id}`, b).then((r) => r.data.data),
  deleteTier: (id) => api.delete(`/v1/loyalty/tiers/${id}`),
  memberships: (params) => api.get("/v1/loyalty/memberships", { params }).then((r) => r.data.data),
  assignMembership: (cid, tierCode) => api.post(`/v1/loyalty/customers/${cid}/membership`, { tierCode }).then((r) => r.data.data),
  // Phase 2 — Referrals
  referrals: (params) => api.get("/v1/loyalty/referrals", { params }).then((r) => r.data.data),
  createReferral: (b) => api.post("/v1/loyalty/referrals", b).then((r) => r.data.data),
  completeReferral: (id) => api.post(`/v1/loyalty/referrals/${id}/complete`).then((r) => r.data.data),
  deleteReferral: (id) => api.delete(`/v1/loyalty/referrals/${id}`),
  // Phase 2 — Coupons & Vouchers
  coupons: (params) => api.get("/v1/loyalty/coupons", { params }).then((r) => r.data.data),
  createCoupon: (b) => api.post("/v1/loyalty/coupons", b).then((r) => r.data.data),
  updateCoupon: (id, b) => api.put(`/v1/loyalty/coupons/${id}`, b).then((r) => r.data.data),
  deleteCoupon: (id) => api.delete(`/v1/loyalty/coupons/${id}`),
  validateCoupon: (code, amount) => api.post("/v1/loyalty/coupons/validate", { code, amount }).then((r) => r.data.data),
  redeemCoupon: (id) => api.post(`/v1/loyalty/coupons/${id}/redeem`).then((r) => r.data.data),
  // Phase 3 — AI Loyalty Agent, Digital Twin, Copilot
  aiInsights: (cid, narrative) => api.get(`/v1/loyalty/ai/customers/${cid}/insights`, { params: { narrative } }).then((r) => r.data.data),
  digitalTwin: (cid) => api.get(`/v1/loyalty/ai/digital-twin/${cid}`).then((r) => r.data.data),
  copilotQuestions: () => api.get("/v1/loyalty/ai/copilot/questions").then((r) => r.data.data),
  copilotAsk: (question) => api.get("/v1/loyalty/ai/copilot", { params: { question } }).then((r) => r.data.data),
  // Phase 4 — Analytics + Omnichannel
  analyticsExecutive: () => api.get("/v1/loyalty/analytics/executive").then((r) => r.data.data),
  analyticsPrograms: () => api.get("/v1/loyalty/analytics/programs").then((r) => r.data.data),
  analyticsCustomers: () => api.get("/v1/loyalty/analytics/customers").then((r) => r.data.data),
  omniConfig: () => api.get("/v1/loyalty/omnichannel/config").then((r) => r.data.data),
  setOmniConfig: (channels) => api.put("/v1/loyalty/omnichannel/config", { channels }).then((r) => r.data.data),
  campaignLinks: () => api.get("/v1/loyalty/omnichannel/campaign-links").then((r) => r.data.data),
  createCampaignLink: (b) => api.post("/v1/loyalty/omnichannel/campaign-links", b).then((r) => r.data.data),
  omniNotify: (b) => api.post("/v1/loyalty/omnichannel/notify", b).then((r) => r.data.data),
};

